import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Activity, LogIn, Upload, Trash2, Download } from 'lucide-react';
import { motion } from 'framer-motion';

const actionIcons = {
    'LOGIN': <LogIn className="w-5 h-5 text-green-500" />,
    'UPLOAD': <Upload className="w-5 h-5 text-blue-500" />,
    'DOWNLOAD': <Download className="w-5 h-5 text-purple-500" />,
    'DELETE': <Trash2 className="w-5 h-5 text-red-500" />,
};

const AuditLogs = () => {
    const { token } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const res = await axios.get('http://localhost:4000/api/audit', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setLogs(res.data.data);
            } catch (error) {
                console.error("Failed to fetch logs", error);
            } finally {
                setLoading(false);
            }
        };
        fetchLogs();
    }, [token]);

    return (
        <Layout>
            <div className="max-w-7xl mx-auto space-y-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
                        <Activity className="mr-3 h-8 w-8 text-blue-600" />
                        Activity Logs
                    </h1>
                    <p className="text-gray-500 mt-2">Track all actions performed in your household securely.</p>
                </div>

                {loading ? (
                    <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div></div>
                ) : (
                    <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-700">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 font-semibold text-xs uppercase tracking-wider">
                                    <tr>
                                        <th className="px-6 py-4">Action</th>
                                        <th className="px-6 py-4">User</th>
                                        <th className="px-6 py-4">Details</th>
                                        <th className="px-6 py-4">Date & Time</th>
                                        <th className="px-6 py-4">IP Address</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700 text-sm">
                                    {logs.map((log, idx) => (
                                        <motion.tr 
                                            key={log._id}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: idx * 0.02 }}
                                            className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 dark:text-white flex items-center gap-2">
                                                {actionIcons[log.action] || <Activity className="w-5 h-5" />}
                                                {log.action}
                                            </td>
                                            <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                                                <div className="flex items-center">
                                                    <div className="h-6 w-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs mr-2">
                                                        {log.user_id?.email?.[0]?.toUpperCase() || 'U'}
                                                    </div>
                                                    {log.user_id?.email?.split('@')[0] || 'Unknown User'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-gray-600 dark:text-gray-300 max-w-xs truncate" title={log.details}>
                                                {log.details}
                                            </td>
                                            <td className="px-6 py-4 text-gray-500">
                                                {new Date(log.timestamp).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-gray-400 font-mono text-xs">
                                                {log.ip_address?.replace('::ffff:', '')}
                                            </td>
                                        </motion.tr>
                                    ))}
                                    {logs.length === 0 && (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                                No activity recorded yet.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default AuditLogs;
